/**
 * Mobile BD — Business Development quick-capture & pipeline view.
 * Reuses existing /api/crm/opportunities endpoints (no new backend).
 * Gated by real RBAC: only visible when the user's effective perms include
 * crm.read=true (role-based OR per-user override).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Handshake, Plus, Search, Loader2, Phone, Building2, X, ChevronRight } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api';

// Must match ProjectsPipeline.jsx / crm_sla.py's DEFAULT_OPP_STAGES exactly —
// this page writes to the same db.opportunities.stage field. A mismatched
// stage string here would move an opportunity to a value no desktop Kanban
// column matches, making it effectively disappear from that board.
const STAGES = ['Enquiry Received', 'Qualification', 'Quotation Sent', 'Negotiation', 'Lost', 'PO Received'];

const stageTone = (s) => {
  const m = {
    'Enquiry Received': 'bg-slate-100 text-slate-700 border-slate-200',
    'Qualification': 'bg-amber-100 text-amber-800 border-amber-200',
    'Quotation Sent': 'bg-teal-100 text-teal-800 border-teal-200',
    'Negotiation': 'bg-amber-100 text-amber-800 border-amber-200',
    'PO Received': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Lost': 'bg-red-100 text-red-800 border-red-200',
  };
  return m[s] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function MobileBD() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/crm/opportunities');
      // /crm/opportunities returns {opportunities: [...]} — unwrap the
      // named array and always default to [] so .filter()/.map() can never
      // explode on an unexpected shape (this was the cause of the
      // "e.filter is not a function" crash on the BD mobile screen).
      const list = Array.isArray(data)
        ? data
        : (data?.opportunities || data?.items || []);
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error('Failed to load opportunities');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter(r => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (r.client || '').toLowerCase().includes(needle) ||
           (r.project_name || '').toLowerCase().includes(needle) ||
           (r.client_email || '').toLowerCase().includes(needle);
  });

  const moveStage = async (opp, newStage) => {
    try {
      await apiPut(`/crm/opportunities/${opp.id}`, { ...opp, stage: newStage });
      toast.success(`Moved to ${newStage}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update stage');
    }
  };

  return (
    <div data-testid="mobile-bd" className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-[#1F7A6E] text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <Link to="/m/home" className="p-1 hover:bg-white/10 rounded-sm"><ArrowLeft size={20} /></Link>
        <Handshake size={20} />
        <div className="flex-1">
          <div className="font-display font-bold text-base">Business Development</div>
          <div className="text-[10px] text-white/70">{filtered.length} opportunities</div>
        </div>
        <button data-testid="bd-new" onClick={() => setShowNew(true)} className="p-1 hover:bg-white/10 rounded-sm">
          <Plus size={20} />
        </button>
      </header>

      <div className="p-3 sticky top-[52px] bg-slate-50 z-[5]">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="bd-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search company, project, email…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
      </div>

      <div className="px-3 space-y-2">
        {loading && <div className="text-center py-8 text-slate-500"><Loader2 className="animate-spin inline" /></div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm">No opportunities match.</div>
        )}
        {!loading && filtered.map(r => (
          <button key={r.id} data-testid={`bd-row-${r.id}`}
            onClick={() => setSelected(r)}
            className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 hover:shadow-sm active:scale-[0.99] transition-all">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.project_name || r.client}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <Building2 size={11} /> {r.client}
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${stageTone(r.stage)}`}>
                {r.stage || 'Enquiry Received'}
              </span>
              <span className="text-sm font-mono font-bold text-[#1F7A6E]">{inr(r.estimated_value)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Detail bottom-sheet */}
      {selected && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={() => setSelected(null)}>
          <div className="bg-white w-full rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg text-[#1F7A6E]">{selected.project_name || selected.client}</div>
                <div className="text-xs text-slate-500 mt-0.5">{selected.client} · {inr(selected.estimated_value)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 -mr-2"><X size={20} /></button>
            </div>
            {selected.client_email && (
              <div className="text-xs text-slate-600 mb-2 break-all">📧 {selected.client_email}</div>
            )}
            {selected.client_phone && (
              <a href={`tel:${selected.client_phone}`} className="text-xs text-[#1F7A6E] mb-3 flex items-center gap-1">
                <Phone size={12} /> {selected.client_phone}
              </a>
            )}
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Move to stage</div>
            <div className="grid grid-cols-2 gap-2">
              {STAGES.map(s => (
                <button key={s} data-testid={`stage-${s}`}
                  onClick={() => moveStage(selected, s)}
                  disabled={s === selected.stage}
                  className={`py-2 px-2 text-xs font-bold uppercase tracking-wider border rounded-sm ${
                    s === selected.stage ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-default' : `${stageTone(s)} hover:opacity-80`
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New lead bottom-sheet */}
      {showNew && (
        <NewBDLead onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function NewBDLead({ onClose, onCreated }) {
  const [form, setForm] = useState({ client: '', project_name: '', client_email: '', client_phone: '', estimated_value: '', source: 'Direct', stage: 'Enquiry Received', probability: 25 });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.client.trim()) return toast.error('Company is required');
    setSaving(true);
    try {
      await apiPost('/crm/opportunities', {
        ...form,
        estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value),
        location: '',
      });
      toast.success('Lead captured');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to capture lead');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={onClose}>
      <div className="bg-white w-full rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-lg text-[#1F7A6E]">New Lead</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {[
          ['client', 'Company *', 'e.g. Sapphire Pvt Ltd'],
          ['project_name', 'Project Name', 'e.g. Tower B – Pune'],
          ['client_email', 'Contact Email', 'name@company.com'],
          ['client_phone', 'Contact Phone', '+91 …'],
          ['estimated_value', 'Estimated Value (₹)', '5000000'],
        ].map(([k, label, ph]) => (
          <label key={k} className="block mb-3">
            <span className="text-xs font-medium text-slate-700">{label}</span>
            <input data-testid={`new-bd-${k}`} value={form[k]}
              type={k === 'estimated_value' ? 'number' : 'text'}
              onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
              placeholder={ph}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
        ))}
        <button data-testid="new-bd-submit" onClick={submit} disabled={saving}
          className="w-full py-3 bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm disabled:opacity-50">
          {saving ? 'Saving…' : 'Capture Lead'}
        </button>
      </div>
    </div>
  );
}
